import { Component, ElementRef, HostListener, OnChanges, SimpleChanges, ViewChild, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Subject, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SyncService } from '../../services/sync-service';
import { FieldContext } from '../../models/field-context';
import { FieldIntegrationStatus, IntegrationOverview } from '../../models/integration-status';
import { FieldDashboardDataService, loadedFieldContext } from '../field-dashboard-data.service';

export type DrawerTab = 'field' | 'all';

/**
 * The Southern Gateway integration drawer (Option A), opened from the "?" after the Field Analysis
 * heading. The dashboard renders it on Southern Gateway only. Closed, it is inert and invisible, so
 * nothing in it can be focused or read out. It stays open under the link editor, whose modal sits
 * above it, and Escape is left to the page while that editor is open.
 *
 * "All integrations" loads the first time it is shown, and again after a link change, since that
 * changes the linked-field counts and cross-client warnings.
 */
@Component({
  selector: 'app-integration-drawer',
  templateUrl: './integration-drawer.component.html',
  standalone: false
})
export class IntegrationDrawerComponent implements OnChanges {
  readonly open = input(false);
  readonly status = input<FieldIntegrationStatus | null>(null);
  readonly loading = input(false);
  readonly failed = input(false);
  readonly closed = output<void>();
  readonly retry = output<void>();

  private readonly syncService = inject(SyncService);
  private readonly dataService = inject(FieldDashboardDataService);

  readonly tab = signal<DrawerTab>('field');
  readonly overview = signal<IntegrationOverview | null>(null);
  readonly overviewLoading = signal(false);
  readonly overviewFailed = signal(false);

  private readonly fieldData = toSignal(this.dataService.fieldData$, { requireSync: true });
  /** Null until it has loaded for this field; the edit actions stay off until then. */
  readonly context = computed<FieldContext | null>(() => loadedFieldContext(this.fieldData()));
  readonly linksFailed = computed(() => !!this.fieldData()?.fieldContextError);
  /** The drawer goes inert under the link editor, so Tab cannot land on it behind the modal. */
  readonly linkEditorOpen = this.dataService.linkEditorOpen;

  readonly subtitle = computed(() => {
    if (this.tab() === 'all') {
      return 'All Southern Gateway sources';
    }
    const data = this.fieldData();
    const status = this.status();
    const field = status?.fieldName || data?.fieldName;
    return [field ? `Field ${field}` : '', data?.farmName, status?.tenant?.name].filter(Boolean).join(' · ');
  });

  @ViewChild('closeButton') private closeButton?: ElementRef<HTMLButtonElement>;
  /** One overview call at a time: a newer request cancels the older, so a stale answer never lands. */
  private readonly overviewRequests = new Subject<void>();

  constructor() {
    this.overviewRequests.pipe(
      switchMap(() => this.syncService.getIntegrationOverview().pipe(
        map(overview => ({ overview })),
        catchError(error => {
          console.error('Error loading the integration overview:', error);
          return of({ overview: null as IntegrationOverview | null });
        })
      )),
      takeUntilDestroyed()
    ).subscribe(({ overview }) => {
      this.overview.set(overview);
      this.overviewFailed.set(overview === null);
      this.overviewLoading.set(false);
    });
    this.dataService.fieldContextReload$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.invalidateOverview());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open()) {
      if (this.tab() === 'all') {
        this.ensureOverview();
      }
      // After the inert attribute is gone, so the close button can take focus.
      setTimeout(() => this.closeButton?.nativeElement.focus());
    }
  }

  selectTab(tab: DrawerTab): void {
    this.tab.set(tab);
    if (tab === 'all') {
      this.ensureOverview();
    }
  }

  loadOverview(): void {
    this.overviewLoading.set(true);
    this.overviewFailed.set(false);
    this.overviewRequests.next();
  }

  close(): void {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open() && !this.dataService.linkEditorOpen()) {
      this.close();
    }
  }

  /** Loads only when nothing is loaded or loading; a failed load is tried again here. */
  private ensureOverview(): void {
    if (!this.overview() && !this.overviewLoading()) {
      this.loadOverview();
    }
  }

  /** A load already under way may predate the change, so it is restarted too. */
  private invalidateOverview(): void {
    this.overview.set(null);
    if (this.overviewLoading() || (this.open() && this.tab() === 'all')) {
      this.loadOverview();
    }
  }
}
